<script lang="ts">
  import {deleteAllSearchQueryStringParameter, setQueryStringParameter, replaceQueryParameter} from "../../url";
  import locationService from "../../services/location-service";
  import LocationSuggestion from "./LocationSuggestion.svelte";
  import type { ArcgisSuggestion, Where } from "../../models/location.interface";
  import {SearchState, WHERE_SEARCH_PARAMS} from "../../services/search-service";
  import type { SearchStateModel } from "../../models/search.interface";
  import type { GeoShapeResult } from "../../models/geo-shape.interface";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let tooltip: string = "Select location from autocomplete or draw a bounding box using the map tool.";

  let whereSearch: Where = {
    name: "",
    magicKey: "",
    bbox: []
  };

  let whereSearchText = "";

  SearchState.subscribe(async (search: SearchStateModel) => {
    if (search.params.has("esriMagicKey")) {
      let shape: GeoShapeResult = await locationService.getShape(search.params.get("esriMagicKey"));
      whereSearch = {
        name: shape.name,
        magicKey: search.params.get("esriMagicKey"),
        bbox: []
      };
    } else if (search.params.has("bbox")) {
      whereSearch = {
        name: null,
        magicKey: null,
        bbox: search.params.get("bbox").split(",").map(Number.parseFloat)
      };
    } else {
      whereSearch = {
        name: null,
        magicKey: null,
        bbox: []
      };
    }

  });

  /* Handling Autocomplete Inputs */
  let suggestions: ArcgisSuggestion[] = [];
  let hiLiteIndex = null;

  let recentSearches: Set<Where> = new Set();
  let recentList = [];

  /* waits on the API call to complete and populates the suggestion array */
  async function handleWhereInput(e) {
    suggestions = await locationService.getSuggestions(e.target.value);
  }

  /* navigation & highlighting in the suggestion dropdown */
  function navigateList(e) {
    if (e.key === "ArrowDown" && hiLiteIndex <= suggestions.length - 1) {
      hiLiteIndex === null ? (hiLiteIndex = 0) : (hiLiteIndex += 1);
    } else if (e.key === "ArrowUp" && hiLiteIndex !== null) {
      hiLiteIndex === 0 ? (hiLiteIndex = suggestions.length - 1) : (hiLiteIndex -= 1);
    } else if (e.key === "Enter") {
      if (hiLiteIndex !== null) {
        const suggestion = suggestions[hiLiteIndex];
        /* check for null value */
        if (suggestion) {
          setInputVal(suggestion.text, suggestion.magicKey);
          handleSubmit(e);
        }
        e.preventDefault(); // Prevent default form submission
      }
      e.preventDefault();
    } else {
      return;
    }
  }

  /* grabs the text from the dropdown and places it into the Location Search input box */
  function setInputVal(suggestionName, suggestionId) {
    whereSearch = {
      name: suggestionName,
      magicKey: suggestionId,
      bbox: []
    };

    clearSuggestions();
    hiLiteIndex = null;
  }

  function clearSuggestions(): void {
    suggestions = [];
    whereSearchText = "";
  }

  /*  allows the user to CLICK on a suggestion OR navigate w/ keyboard in the dropdown list */
  async function handleSubmit(e) {

    /* e.submitter is the targeted button */
    if (e.type === "submit") { // Clicked a suggestion
      setInputVal(e.submitter.value, e.submitter.id);
    } else if (e.type !== "keydown") { // Shouldn't be able to get here
      alert("Error: No value to submit in the Sidebar Location Search");
      return;
    } // If they used the Enter key this block is skipped

    if (whereSearch.magicKey) {
      replaceQueryParameter(WHERE_SEARCH_PARAMS, "esriMagicKey", whereSearch.magicKey);
    } else {
      replaceQueryParameter(WHERE_SEARCH_PARAMS, "bbox", whereSearch.bbox.toString());
    }

    recentSearches.add(whereSearch);
    recentList = [...recentSearches];

    setTimeout(clearSuggestions, 1000);
  }
</script>

<svelte:window on:keydown={navigateList} />

<li class="mb-1">
  <fieldset>
    <legend class="mb-2 fs-5">
      Where
      <!-- tooltip icon -->
      <TooltipHybridButton
        searchUrlQueryParam="where"
        tooltip={tooltip}
      />
      <!--      <button-->
      <!--        type="button"-->
      <!--        id="whereToolTip"-->
      <!--        class="btn btn-link mb-1 ps-1"-->
      <!--        style="color: #1E2125;"-->
      <!--        aria-describedby="locationSearchToolTipContent"-->
      <!--      >-->
      <!--        <i class="bi bi-question-circle-fill" aria-hidden="true"></i>-->
      <!--      </button>-->
      <!--      <Tooltip-->
      <!--        id="locationSearchToolTipContent"-->
      <!--        target={`whereToolTip`}-->
      <!--        placement="right"-->
      <!--        role="tooltip"-->
      <!--      >-->
      <!--        <p>Select location from autocomplete or draw a bounding box using the map tool.</p>-->
      <!--      </Tooltip>-->
    </legend>

    <!-- location search input w/ floating label -->
    <form id="country-input" autocomplete="off" on:submit|preventDefault={handleSubmit}>
      <div class="form-floating margin-2">
        <input
          type="text"
          class="form-control nosubmit"
          id="locationSearchInput"
          placeholder="Location Search"
          bind:value={whereSearchText}
          on:input={(e) => handleWhereInput(e)}
        />
        <label for="locationSearchInput">Ex: North Carolina</label>

        <!-- here we're dynamically generating the suggestions list -->
        {#if suggestions && suggestions.length > 0}
          <ul class="suggestions list-group bg-white mt-1 small shadow-lg position-absolute">
            {#each suggestions as suggestion, i}
              <LocationSuggestion
                {suggestion}
                highlighted={i === hiLiteIndex}
                on:click={() => setInputVal(suggestion.text, suggestion.magicKey)}
              />
            {/each}
          </ul>
        {/if}
      </div>
    </form>
  </fieldset>

  <!-- Displays pills of the current search parameters -->
  {#if $SearchState.params.has("bbox") || $SearchState.params.has("esriMagicKey")}
    <div class="d-flex py-1">
      <span
        style="overflow-x: hidden; text-overflow: ellipsis"
        class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1"
        role="status"
        aria-label="where search pill: {whereSearch.name ? whereSearch.name : ''} {whereSearch.bbox ? whereSearch.bbox : ''}"
      >
        <button
          type="button"
          class="btn p-0 m-0"
          on:click={() => {
            deleteAllSearchQueryStringParameter("bbox");
            deleteAllSearchQueryStringParameter("esriMagicKey");
            whereSearch = {name: null, magicKey: null, bbox: []};
          }}
        >
          <i class="bi bi-x text-bg-dark"
             aria-label="Delete {whereSearch.name ? whereSearch.name : ''} {whereSearch.bbox ? whereSearch.bbox : ''}}"
          />
        </button>
        {whereSearch.name ? whereSearch.name : ''} {whereSearch.bbox ? whereSearch.bbox : ''}
      </span>
    </div>
  {/if}
</li>

<style>
  .suggestions {
    z-index: 3;
  }
</style>
